import type { AdminGameListItem, GameBulkUpdate, Paginated } from "@lucky-try/contracts";
import { PlusIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { adminColumnHelper, DataTable } from "../../components/ui/data-table";
import { Checkbox, Input, Select } from "../../components/ui/form";
import { Badge, ErrorState, Pagination, PageHeader, Toolbar } from "../../components/ui/misc";
import { cn } from "../../lib/cn";
import { api, errorMessage } from "../../lib/api";
import { formatDateTime, relativeTime } from "../../lib/format";
import { categoriesQuery } from "../../lib/queries";
import { useCan } from "../../lib/session";

const column = adminColumnHelper<AdminGameListItem>();

const providerLabel: Record<string, string> = {
  gamedistribution: "GameDistribution",
  gamepush: "GamePush",
  external: "External",
  self_hosted: "Self-hosted",
};

const statusLabel: Record<AdminGameListItem["status"], string> = {
  published: "Live",
  draft: "Draft",
  archived: "Archived",
};
const statusDot: Record<AdminGameListItem["status"], string> = {
  published: "bg-accent",
  draft: "bg-gold",
  archived: "bg-faint",
};
const statusText: Record<AdminGameListItem["status"], string> = {
  published: "text-accent-text",
  draft: "text-gold-text",
  archived: "text-muted",
};

export function GamesPage() {
  const search = useSearch({ from: "/authed/games" });
  const navigate = useNavigate({ from: "/games" });
  const queryClient = useQueryClient();
  const canWrite = useCan("catalog.write");
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [term, setTerm] = useState(search.q ?? "");

  const categories = useQuery(categoriesQuery);
  const games = useQuery({
    queryKey: ["games", search],
    queryFn: () =>
      api<Paginated<AdminGameListItem>>("/admin/games", {
        query: { ...search, pageSize: 50 },
      }),
  });

  const bulk = useMutation({
    mutationFn: (body: GameBulkUpdate) =>
      api<{ updated: number }>("/admin/games/bulk", { method: "PATCH", body }),
    onSuccess: async ({ updated }) => {
      setSelection({});
      await queryClient.invalidateQueries({ queryKey: ["games"] });
      toast.success(`${updated} game${updated === 1 ? "" : "s"} updated`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const selectedIds = useMemo(
    () =>
      Object.entries(selection)
        .filter(([, checked]) => checked)
        .map(([id]) => Number(id)),
    [selection],
  );

  const columns = useMemo(
    () => [
      ...(canWrite
        ? [
            column.display({
              id: "select",
              header: ({ table }) => (
                <Checkbox
                  aria-label="Select all rows"
                  checked={table.getIsAllRowsSelected()}
                  ref={(node) => {
                    if (node) node.indeterminate = table.getIsSomeRowsSelected();
                  }}
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  aria-label={`Select ${row.original.name}`}
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(event) => event.stopPropagation()}
                />
              ),
            }),
          ]
        : []),
      column.accessor("name", {
        header: "Game",
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <span className="cover size-7 shrink-0">
              <span className="text-faint absolute inset-0 grid place-items-center font-semibold">
                {row.original.name.slice(0, 1)}
              </span>
              {row.original.iconUrl && (
                <img src={row.original.iconUrl} alt="" loading="lazy" className="relative" />
              )}
            </span>
            <div className="min-w-0">
              <p className="text-ink truncate font-medium">{row.original.name}</p>
              <p className="text-muted text-caption truncate">/{row.original.slug}</p>
            </div>
          </div>
        ),
      }),
      column.accessor("status", {
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <span className="text-caption flex items-center gap-1.5 whitespace-nowrap">
              <span className={cn("size-1.5 rounded-full", statusDot[status])} />
              <span className={statusText[status]}>{statusLabel[status]}</span>
            </span>
          );
        },
      }),
      column.accessor("categoryName", { header: "Category" }),
      column.display({
        id: "delivery",
        header: "Delivery",
        cell: ({ row }) => (
          <span className="text-body text-caption">
            {row.original.providers
              .map((provider) => providerLabel[provider] ?? provider)
              .join(", ") || "—"}
            {row.original.markets.length > 1 && (
              <span className="text-faint"> · {row.original.markets.join("/")}</span>
            )}
          </span>
        ),
      }),
      column.display({
        id: "flags",
        header: "Flags",
        cell: ({ row }) => (
          <div className="flex gap-1">
            {row.original.isPopular && <Badge>Popular</Badge>}
            {row.original.isNew && <Badge tone="info">New</Badge>}
            {row.original.isOriginal && <Badge tone="success">Original</Badge>}
            {!row.original.adsenseEligible && (
              <span className="text-caption text-faint self-center">no ads</span>
            )}
          </div>
        ),
      }),
      column.accessor("updatedAt", {
        header: "Updated",
        cell: ({ getValue }) => (
          <span className="text-muted text-caption tabular" title={formatDateTime(getValue())}>
            {relativeTime(getValue())}
          </span>
        ),
      }),
    ],
    [canWrite],
  );

  function setSearch(patch: Partial<typeof search>) {
    void navigate({ search: (previous) => ({ ...previous, page: 1, ...patch }) });
  }

  if (games.error) return <ErrorState error={games.error} onRetry={() => void games.refetch()} />;

  return (
    <>
      <PageHeader
        title="Games"
        description="Catalog entries, delivery variants and publication state."
        actions={
          canWrite && (
            <Link to="/games/new">
              <Button variant="primary">
                <PlusIcon /> New game
              </Button>
            </Link>
          )
        }
      />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch({ q: term || undefined });
          }}
        >
          <Input
            placeholder="Search name, slug or id"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="w-64"
          />
          <Button type="submit">Search</Button>
        </form>
        <Select
          value={search.status ?? ""}
          onChange={(event) =>
            setSearch({ status: (event.target.value || undefined) as typeof search.status })
          }
          aria-label="Status filter"
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </Select>
        <Select
          value={search.categoryId ?? ""}
          onChange={(event) =>
            setSearch({ categoryId: event.target.value ? Number(event.target.value) : undefined })
          }
          aria-label="Category filter"
        >
          <option value="">All categories</option>
          {categories.data?.items.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
        <Select
          value={search.provider ?? ""}
          onChange={(event) =>
            setSearch({ provider: (event.target.value || undefined) as typeof search.provider })
          }
          aria-label="Provider filter"
        >
          <option value="">All providers</option>
          <option value="gamedistribution">GameDistribution</option>
          <option value="gamepush">GamePush</option>
          <option value="external">External</option>
          <option value="self_hosted">Self-hosted</option>
        </Select>
        <Select
          value={`${search.sort}:${search.dir}`}
          onChange={(event) => {
            const [sort, dir] = event.target.value.split(":");
            setSearch({ sort: sort as typeof search.sort, dir: dir as typeof search.dir });
          }}
          aria-label="Sort"
        >
          <option value="updatedAt:desc">Recently updated</option>
          <option value="createdAt:desc">Recently added</option>
          <option value="name:asc">Name A–Z</option>
          <option value="sortOrder:asc">Manual order</option>
        </Select>
      </Toolbar>

      {selectedIds.length > 0 && (
        <div className="border-accent/30 bg-accent/10 text-caption mb-3 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2">
          <span className="text-ink font-medium">{selectedIds.length} selected</span>
          <Button
            size="sm"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ ids: selectedIds, patch: { status: "published" } })}
          >
            Publish
          </Button>
          <Button
            size="sm"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ ids: selectedIds, patch: { status: "draft" } })}
          >
            Move to draft
          </Button>
          <Button
            size="sm"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ ids: selectedIds, patch: { status: "archived" } })}
          >
            Archive
          </Button>
          <Button
            size="sm"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ ids: selectedIds, patch: { isPopular: true } })}
          >
            Mark popular
          </Button>
          <Button
            size="sm"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ ids: selectedIds, patch: { isNew: false } })}
          >
            Clear “new”
          </Button>
          <Select
            className="w-44"
            value=""
            aria-label="Move to category"
            disabled={bulk.isPending}
            onChange={(event) =>
              event.target.value &&
              bulk.mutate({ ids: selectedIds, patch: { categoryId: Number(event.target.value) } })
            }
          >
            <option value="">Move to category…</option>
            {categories.data?.items.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="ghost" onClick={() => setSelection({})}>
            Clear selection
          </Button>
        </div>
      )}

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        <DataTable
          columns={columns}
          data={games.data?.items ?? []}
          getRowId={(row) => String(row.id)}
          loading={games.isFetching}
          empty="No games match these filters."
          onRowClick={(row) =>
            void navigate({ to: "/games/$gameId", params: { gameId: String(row.id) } })
          }
          {...(canWrite ? { rowSelection: selection, onRowSelectionChange: setSelection } : {})}
        />
        <Pagination
          page={games.data?.page ?? 1}
          pageSize={games.data?.pageSize ?? 50}
          total={games.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>
    </>
  );
}
